import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Alert,
} from '@mui/material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import EmailIcon from '@mui/icons-material/Email';

/**
 * Popup che avvisa l'utente quando il trial sta per scadere (entro 3 giorni).
 * Si chiude per 24h via localStorage (per non spammare ad ogni refresh).
 * Reset automatico se la dataScadenza cambia (es. admin estende il trial).
 *
 * Props:
 *  - trialEndsAt: Firestore Timestamp del termine trial (può essere null)
 */
const WARNING_THRESHOLD_DAYS = 3;
const SUPPORT_EMAIL = 'info@motorsportsuite.com';

export default function TrialEndingDialog({ trialEndsAt }) {
  const { t } = useTranslation('main-layout');
  const [open, setOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    if (!trialEndsAt) return;
    const endMs = trialEndsAt.toDate?.()?.getTime?.();
    if (!endMs) return;

    const nowMs = Date.now();
    const msLeft = endMs - nowMs;
    const days = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

    // Non mostrare se trial è già scaduto (gestito altrove con blocco totale)
    // né se mancano più di N giorni
    if (days <= 0 || days > WARNING_THRESHOLD_DAYS) return;

    // Chiave dismiss per data scadenza specifica + giornata corrente
    // (mostra max una volta al giorno per la stessa scadenza)
    const todayKey = new Date().toISOString().split('T')[0];
    const dismissKey = `trialEnding_${endMs}_${todayKey}`;
    if (localStorage.getItem(dismissKey) === '1') return;

    setDaysLeft(days);
    setOpen(true);
  }, [trialEndsAt]);

  const handleClose = () => {
    if (trialEndsAt) {
      const endMs = trialEndsAt.toDate?.()?.getTime?.();
      const todayKey = new Date().toISOString().split('T')[0];
      if (endMs) localStorage.setItem(`trialEnding_${endMs}_${todayKey}`, '1');
    }
    setOpen(false);
  };

  const handleContact = () => {
    const subject = encodeURIComponent('Richiesta attivazione abbonamento MotorSportSuite');
    const body = encodeURIComponent(
      `Ciao,\n\nVorrei attivare un piano per il mio team prima della scadenza del trial.\n\n` +
      `Grazie,\n`
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  if (!open || daysLeft == null) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <AccessAlarmIcon color="warning" />
        {t('trial_ending.title', { defaultValue: 'Il tuo periodo di prova sta per scadere' })}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>
            {daysLeft === 1
              ? t('trial_ending.days_one', { defaultValue: 'Il tuo trial scade domani.' })
              : t('trial_ending.days_other', {
                  defaultValue: 'Il tuo trial scade tra {{count}} giorni.',
                  count: daysLeft,
                })}
          </strong>
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('trial_ending.body', {
            defaultValue:
              'Per continuare a usare MotorSportSuite senza interruzioni — mantenendo tutti i tuoi dati, setup, telemetria e analisi AI — contattaci per attivare un piano a pagamento. Ti risponderemo entro 24 ore in orario lavorativo.',
          })}
        </Typography>
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>{t('trial_ending.contact_label', { defaultValue: 'Email di contatto:' })}</strong>
            {' '}{SUPPORT_EMAIL}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          {t('trial_ending.dismiss', { defaultValue: 'Più tardi' })}
        </Button>
        <Button variant="contained" startIcon={<EmailIcon />} onClick={handleContact}>
          {t('trial_ending.contact', { defaultValue: 'Contattaci per acquistare' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
